import React, { useState, useRef, useCallback } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import { supabase } from '../../../lib/supabase';
import { useAuth } from '../../../contexts/AuthContext';
import toast from 'react-hot-toast';

// Contact fields that can be mapped from CSV columns
const CONTACT_FIELDS = [
  { key: 'first_name', label: 'First Name', required: true },
  { key: 'last_name', label: 'Last Name', required: false },
  { key: 'email', label: 'Email', required: false },
  { key: 'phone', label: 'Phone', required: false },
  { key: 'title', label: 'Job Title', required: false },
  { key: 'company_name', label: 'Company Name', required: false },
  { key: 'address', label: 'Street Address', required: false },
  { key: 'city', label: 'City', required: false },
  { key: 'state', label: 'State', required: false },
  { key: 'zip', label: 'ZIP Code', required: false },
  { key: 'lead_source', label: 'Lead Source', required: false },
  { key: 'payment_method', label: 'Payment Method', required: false },
  { key: 'notes', label: 'Notes', required: false },
  { key: 'skip', label: '— Skip this column —', required: false },
];

// Try to auto-match CSV header to a contact field
const autoMatch = (header) => {
  const h = header?.toLowerCase()?.trim()?.replace(/[\s_-]+/g, '_');
  const map = {
    first_name: ['first_name', 'firstname', 'first', 'given_name', 'givenname', 'name'],
    last_name: ['last_name', 'lastname', 'last', 'surname', 'family_name'],
    email: ['email', 'email_address', 'emailaddress', 'e_mail', 'mail'],
    phone: ['phone', 'phone_number', 'phonenumber', 'mobile', 'cell', 'telephone', 'tel'],
    title: ['title', 'job_title', 'jobtitle', 'position', 'role'],
    company_name: ['company', 'company_name', 'companyname', 'organization', 'org', 'business', 'account'],
    address: ['address', 'street', 'street_address', 'streetaddress', 'addr', 'address_1', 'address1'],
    city: ['city', 'town', 'municipality'],
    state: ['state', 'province', 'region', 'st'],
    zip: ['zip', 'zip_code', 'zipcode', 'postal', 'postal_code', 'postalcode'],
    lead_source: ['lead_source', 'leadsource', 'source', 'how_found', 'referral_source', 'marketing_source'],
    payment_method: ['payment_method', 'paymentmethod', 'payment', 'pay_method', 'preferred_payment'],
    notes: ['notes', 'note', 'comments', 'comment', 'description', 'memo', 'remarks'],
  };
  for (const [field, aliases] of Object.entries(map)) {
    if (aliases?.includes(h)) return field;
  }
  return 'skip';
};

const parseCSV = (text) => {
  const lines = text?.trim()?.split(/\r?\n/);
  if (!lines?.length) return { headers: [], rows: [] };

  const parseRow = (line) => {
    const result = [];
    let current = '';
    let inQuotes = false;
    for (let i = 0; i < line?.length; i++) {
      const ch = line?.[i];
      if (ch === '"') {
        if (inQuotes && line?.[i + 1] === '"') { current += '"'; i++; }
        else inQuotes = !inQuotes;
      } else if (ch === ',' && !inQuotes) {
        result?.push(current?.trim());
        current = '';
      } else {
        current += ch;
      }
    }
    result?.push(current?.trim());
    return result;
  };

  const headers = parseRow(lines?.[0]);
  const rows = lines?.slice(1)?.filter(l => l?.trim())?.map(parseRow);
  return { headers, rows };
};

const STEPS = ['Upload', 'Map Columns', 'Preview', 'Done'];

const CSVImportModal = ({ isOpen, onClose, onImportComplete }) => {
  const { profile } = useAuth();
  const fileInputRef = useRef(null);
  const [step, setStep] = useState(0);
  const [dragOver, setDragOver] = useState(false);
  const [fileName, setFileName] = useState('');
  const [headers, setHeaders] = useState([]);
  const [rows, setRows] = useState([]);
  const [mapping, setMapping] = useState({});
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState(null);
  const [error, setError] = useState('');

  const reset = () => {
    setStep(0);
    setDragOver(false);
    setFileName('');
    setHeaders([]);
    setRows([]);
    setMapping({});
    setImporting(false);
    setImportResult(null);
    setError('');
  };

  const handleClose = () => {
    reset();
    onClose();
  };

  const processFile = (file) => {
    if (!file) return;
    if (!file?.name?.toLowerCase()?.endsWith('.csv')) {
      setError('Please upload a .csv file');
      return;
    }
    setError('');
    setFileName(file?.name);
    const reader = new FileReader();
    reader.onload = (e) => {
      const { headers: h, rows: r } = parseCSV(e?.target?.result);
      if (!h?.length) { setError('Could not parse CSV. Make sure it has a header row.'); return; }
      setHeaders(h);
      setRows(r);
      const autoMapping = {};
      h?.forEach(header => { autoMapping[header] = autoMatch(header); });
      setMapping(autoMapping);
      setStep(1);
    };
    reader?.readAsText(file);
  };

  const handleFileChange = (e) => processFile(e?.target?.files?.[0]);

  const handleDrop = useCallback((e) => {
    e?.preventDefault();
    setDragOver(false);
    processFile(e?.dataTransfer?.files?.[0]);
  }, []);

  const handleDragOver = (e) => { e?.preventDefault(); setDragOver(true); };
  const handleDragLeave = () => setDragOver(false);

  // Build preview rows from mapping
  const buildPreviewContacts = () => {
    return rows?.slice(0, 5)?.map(row => {
      const contact = {};
      headers?.forEach((header, idx) => {
        const field = mapping?.[header];
        if (field && field !== 'skip') contact[field] = row?.[idx] || '';
      });
      return contact;
    });
  };

  const handleImport = async () => {
    if (!profile?.org_id) return;
    setImporting(true);
    setError('');

    let successCount = 0;
    let failCount = 0;
    const errors = [];

    // Collect unique company names to create accounts first
    const companyNames = new Set();
    rows?.forEach(row => {
      headers?.forEach((header, idx) => {
        if (mapping?.[header] === 'company_name' && row?.[idx]?.trim()) {
          companyNames?.add(row?.[idx]?.trim());
        }
      });
    });

    // Create or find accounts for company names
    const accountMap = {};
    for (const companyName of companyNames) {
      try {
        // Check if account already exists
        const { data: existing } = await supabase
          ?.from('accounts')
          ?.select('id')
          ?.eq('org_id', profile?.org_id)
          ?.ilike('name', companyName)
          ?.limit(1);

        if (existing?.length > 0) {
          accountMap[companyName] = existing?.[0]?.id;
        } else {
          const { data: newAccount } = await supabase
            ?.from('accounts')
            ?.insert({ org_id: profile?.org_id, name: companyName, owner_id: profile?.id })
            ?.select('id')
            ?.single();
          if (newAccount?.id) accountMap[companyName] = newAccount?.id;
        }
      } catch (_) {}
    }

    // Import contacts in batches of 50
    const BATCH_SIZE = 50;
    for (let i = 0; i < rows?.length; i += BATCH_SIZE) {
      const batch = rows?.slice(i, i + BATCH_SIZE);
      const contactsBatch = batch?.map(row => {
        const contact = { org_id: profile?.org_id, owner_id: profile?.id };
        headers?.forEach((header, idx) => {
          const field = mapping?.[header];
          if (!field || field === 'skip') return;
          if (field === 'company_name') {
            const cName = row?.[idx]?.trim();
            if (cName && accountMap?.[cName]) contact.account_id = accountMap?.[cName];
          } else {
            contact[field] = row?.[idx]?.trim() || '';
          }
        });
        // Ensure first_name is set
        if (!contact?.first_name) contact.first_name = 'Unknown';
        // Build full address from parts if address not already set
        if (!contact?.address && (contact?.city || contact?.state || contact?.zip)) {
          const parts = [contact?.address, contact?.city, contact?.state, contact?.zip]?.filter(Boolean);
          // address field stays as street only; full address for display is built in drawer
        }
        return contact;
      });

      try {
        const { data, error: insertError } = await supabase
          ?.from('contacts')
          ?.insert(contactsBatch)
          ?.select('id');
        if (insertError) {
          failCount += batch?.length;
          errors?.push(insertError?.message);
        } else {
          successCount += data?.length || 0;
        }
      } catch (err) {
        failCount += batch?.length;
        errors?.push(err?.message);
      }
    }

    setImportResult({ successCount, failCount, errors: errors?.slice(0, 3) });
    setImporting(false);
    setStep(3);

    if (successCount > 0) {
      toast?.success(`Imported ${successCount} contact${successCount !== 1 ? 's' : ''} successfully`);
      onImportComplete?.();
    }
  };

  const mappedFieldsCount = Object.values(mapping)?.filter(v => v && v !== 'skip')?.length;
  const previewContacts = buildPreviewContacts();

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={handleClose} />
      <div className="relative bg-card border border-border rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-border flex-shrink-0">
          <div>
            <h2 className="text-lg font-semibold text-foreground">Import Contacts from CSV</h2>
            <p className="text-sm text-muted-foreground mt-0.5">Migrate contacts from HouseCall Pro, Jobber, or any platform</p>
          </div>
          <button onClick={handleClose} className="p-2 rounded-lg hover:bg-muted transition-colors text-muted-foreground hover:text-foreground">
            <Icon name="X" size={18} />
          </button>
        </div>

        {/* Step Indicator */}
        <div className="px-6 py-3 border-b border-border flex-shrink-0">
          <div className="flex items-center space-x-2">
            {STEPS?.map((label, idx) => (
              <React.Fragment key={label}>
                <div className="flex items-center space-x-1.5">
                  <div className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-semibold transition-colors ${
                    idx < step ? 'bg-primary text-primary-foreground' :
                    idx === step ? 'bg-primary text-primary-foreground': 'bg-muted text-muted-foreground'
                  }`}>
                    {idx < step ? <Icon name="Check" size={12} /> : idx + 1}
                  </div>
                  <span className={`text-xs font-medium hidden sm:block ${idx === step ? 'text-foreground' : 'text-muted-foreground'}`}>{label}</span>
                </div>
                {idx < STEPS?.length - 1 && <div className={`flex-1 h-px ${idx < step ? 'bg-primary' : 'bg-border'}`} />}
              </React.Fragment>
            ))}
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {/* Step 0: Upload */}
          {step === 0 && (
            <div className="space-y-4">
              <div
                className={`border-2 border-dashed rounded-xl p-10 text-center cursor-pointer transition-colors ${
                  dragOver ? 'border-primary bg-primary/5' : 'border-border hover:border-primary/50 hover:bg-muted/30'
                }`}
                onDrop={handleDrop}
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
                onClick={() => fileInputRef?.current?.click()}
              >
                <div className="w-14 h-14 rounded-full bg-primary/10 flex items-center justify-center mx-auto mb-4">
                  <Icon name="Upload" size={24} className="text-primary" />
                </div>
                <p className="text-foreground font-medium mb-1">Drop your CSV file here</p>
                <p className="text-sm text-muted-foreground mb-4">or click to browse</p>
                <Button variant="outline" size="sm" onClick={(e) => { e?.stopPropagation(); fileInputRef?.current?.click(); }}>
                  <Icon name="FileText" size={14} className="mr-2" />
                  Choose CSV File
                </Button>
                <input ref={fileInputRef} type="file" accept=".csv" className="hidden" onChange={handleFileChange} />
              </div>

              {error && (
                <div className="p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg text-sm text-red-600 dark:text-red-400 flex items-center space-x-2">
                  <Icon name="AlertCircle" size={14} />
                  <span>{error}</span>
                </div>
              )}

              <div className="bg-muted/40 rounded-xl p-4 space-y-2">
                <p className="text-sm font-medium text-foreground flex items-center space-x-2">
                  <Icon name="Info" size={14} className="text-primary" />
                  <span>Supported platforms</span>
                </p>
                <div className="flex flex-wrap gap-2">
                  {['HouseCall Pro', 'Jobber', 'ServiceTitan', 'Google Contacts', 'Excel / Sheets', 'Any CSV']?.map(p => (
                    <span key={p} className="px-2.5 py-1 bg-background border border-border rounded-full text-xs text-muted-foreground">{p}</span>
                  ))}
                </div>
                <p className="text-xs text-muted-foreground pt-1">Export your contacts as CSV from any platform and upload here. The first row must be column headers.</p>
              </div>
            </div>
          )}

          {/* Step 1: Map Columns */}
          {step === 1 && (
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-foreground">{fileName}</p>
                  <p className="text-xs text-muted-foreground">{rows?.length} rows detected · {headers?.length} columns</p>
                </div>
                <span className="text-xs text-muted-foreground bg-muted px-2.5 py-1 rounded-full">{mappedFieldsCount} field{mappedFieldsCount !== 1 ? 's' : ''} mapped</span>
              </div>

              <div className="border border-border rounded-xl overflow-hidden">
                <div className="grid grid-cols-2 gap-0 bg-muted/50 px-4 py-2.5 border-b border-border">
                  <span className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">CSV Column</span>
                  <span className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">Maps To</span>
                </div>
                <div className="divide-y divide-border max-h-72 overflow-y-auto">
                  {headers?.map(header => (
                    <div key={header} className="grid grid-cols-2 gap-4 px-4 py-3 items-center hover:bg-muted/20 transition-colors">
                      <div>
                        <p className="text-sm font-medium text-foreground truncate">{header}</p>
                        <p className="text-xs text-muted-foreground truncate">{rows?.[0]?.[headers?.indexOf(header)] || '—'}</p>
                      </div>
                      <select
                        value={mapping?.[header] || 'skip'}
                        onChange={(e) => setMapping(prev => ({ ...prev, [header]: e?.target?.value }))}
                        className="w-full text-sm bg-background border border-border rounded-lg px-3 py-1.5 focus:outline-none focus:ring-2 focus:ring-primary text-foreground"
                      >
                        {CONTACT_FIELDS?.map(f => (
                          <option key={f?.key} value={f?.key}>{f?.label}{f?.required ? ' *' : ''}</option>
                        ))}
                      </select>
                    </div>
                  ))}
                </div>
              </div>

              {!Object.values(mapping)?.includes('first_name') && (
                <div className="p-3 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg text-sm text-amber-700 dark:text-amber-400 flex items-center space-x-2">
                  <Icon name="AlertTriangle" size={14} />
                  <span>Map at least one column to <strong>First Name</strong> to continue.</span>
                </div>
              )}
            </div>
          )}

          {/* Step 2: Preview */}
          {step === 2 && (
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <p className="text-sm text-muted-foreground">Showing first {Math.min(5, rows?.length)} of <strong className="text-foreground">{rows?.length}</strong> contacts to import</p>
              </div>
              <div className="border border-border rounded-xl overflow-hidden">
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-muted/50 border-b border-border">
                      <tr>
                        {Object.entries(mapping)?.filter(([, v]) => v && v !== 'skip')?.map(([header, field]) => (
                          <th key={header} className="px-3 py-2.5 text-left text-xs font-semibold text-muted-foreground whitespace-nowrap">
                            {CONTACT_FIELDS?.find(f => f?.key === field)?.label || field}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-border">
                      {previewContacts?.map((contact, idx) => (
                        <tr key={idx} className="hover:bg-muted/20">
                          {Object.entries(mapping)?.filter(([, v]) => v && v !== 'skip')?.map(([, field]) => (
                            <td key={field} className="px-3 py-2.5 text-foreground whitespace-nowrap max-w-[160px] truncate">
                              {contact?.[field] || <span className="text-muted-foreground">—</span>}
                            </td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
              {rows?.length > 5 && (
                <p className="text-xs text-muted-foreground text-center">+ {rows?.length - 5} more contacts will be imported</p>
              )}
              <div className="p-3 bg-primary/5 border border-primary/20 rounded-lg text-sm text-foreground flex items-start space-x-2">
                <Icon name="Info" size={14} className="text-primary mt-0.5 flex-shrink-0" />
                <span>Duplicate contacts will be added as new entries. Company names will be automatically created as Accounts if they don't exist yet.</span>
              </div>
            </div>
          )}

          {/* Step 3: Done */}
          {step === 3 && importResult && (
            <div className="text-center py-6 space-y-4">
              {importResult?.successCount > 0 ? (
                <div className="w-16 h-16 rounded-full bg-green-100 dark:bg-green-900/30 flex items-center justify-center mx-auto">
                  <Icon name="CheckCircle" size={32} className="text-green-600 dark:text-green-400" />
                </div>
              ) : (
                <div className="w-16 h-16 rounded-full bg-red-100 dark:bg-red-900/30 flex items-center justify-center mx-auto">
                  <Icon name="XCircle" size={32} className="text-red-600 dark:text-red-400" />
                </div>
              )}
              <div>
                <h3 className="text-xl font-semibold text-foreground">
                  {importResult?.successCount > 0 ? 'Import Complete!' : 'Import Failed'}
                </h3>
                <p className="text-muted-foreground mt-1">
                  {importResult?.successCount > 0
                    ? `${importResult?.successCount} contact${importResult?.successCount !== 1 ? 's' : ''} imported successfully`
                    : 'No contacts were imported'}
                  {importResult?.failCount > 0 && `, ${importResult?.failCount} failed`}
                </p>
              </div>
              {importResult?.errors?.length > 0 && (
                <div className="text-left p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg text-xs text-red-600 dark:text-red-400 space-y-1">
                  {importResult?.errors?.map((e, i) => <p key={i}>{e}</p>)}
                </div>
              )}
              <div className="flex items-center justify-center space-x-3 pt-2">
                <Button variant="outline" onClick={() => { reset(); }}>
                  <Icon name="Upload" size={14} className="mr-2" />
                  Import Another File
                </Button>
                <Button onClick={handleClose}>
                  <Icon name="Users" size={14} className="mr-2" />
                  View Contacts
                </Button>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        {step < 3 && (
          <div className="flex items-center justify-between px-6 py-4 border-t border-border flex-shrink-0">
            <Button
              variant="ghost"
              onClick={step === 0 ? handleClose : () => setStep(s => s - 1)}
              disabled={importing}
            >
              {step === 0 ? 'Cancel' : (
                <>
                  <Icon name="ChevronLeft" size={14} className="mr-1" />
                  Back
                </>
              )}
            </Button>

            <div className="flex items-center space-x-3">
              {step === 0 && (
                <p className="text-xs text-muted-foreground">No file selected yet</p>
              )}
              {step === 1 && (
                <Button
                  onClick={() => setStep(2)}
                  disabled={!Object.values(mapping)?.includes('first_name')}
                >
                  Preview Import
                  <Icon name="ChevronRight" size={14} className="ml-1" />
                </Button>
              )}
              {step === 2 && (
                <Button onClick={handleImport} disabled={importing}>
                  {importing ? (
                    <>
                      <Icon name="Loader2" size={14} className="mr-2 animate-spin" />
                      Importing {rows?.length} contacts...
                    </>
                  ) : (
                    <>
                      <Icon name="Download" size={14} className="mr-2" />
                      Import {rows?.length} Contact{rows?.length !== 1 ? 's' : ''}
                    </>
                  )}
                </Button>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default CSVImportModal;
